import React, { useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useUserContext } from 'providers/UserProvider';
import { usePoDetailContext } from 'providers/PoDetailProvider';

import useExcelUpload from 'hooks/useExcelUpload';

import Table from 'components/common/Table';
import IconButton from 'components/common/IconButton';
import PurchaseOrderLineAdditionalHeader from 'components/purchase-order/purchase-order/PurchaseOrderDetails/PurchaseOrderLineAdditionalHeader';
import PurchaseOrderLineEditModal from 'components/purchase-order/purchase-order/PurchaseOrderDetails/PurchaseOrderLineEditModal';

import { purchaseOrderApi, bulkActionApi } from 'api';

import {
  PURCHASE_ORDER_LINE_LIST_COLUMNS,
  PURCHASE_ORDER_LINE_FILTER_OPTIONS,
} from 'constants/purchaseOrder';
import paths from '../../../../routes/paths';

const PurchaseOrderLines = ({ onPoLineUpdate }) => {
  const { purchaseOrderId } = useParams();
  const { poData } = usePoDetailContext();

  const tableRef = useRef();
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const navigate = useNavigate();
  const { selectedTenant, selectedWarehouse } = useUserContext();

  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;

  // PO Lines Upload
  const handlePOLineUpload = async file => {
    try {
      const formData = new FormData();

      formData.append('action_type', 'po_line_create');
      formData.append('warehouse_id', selectedWarehouse?.id);
      formData.append('file', file);

      await bulkActionApi.bulkFileUpload(formData);
      toast.success('File uploaded successfully');
      navigate(paths.bulkAction(tenant, warehouse, 'po_line_create'));

      onPoLineUpdate?.();
    } catch (error) {
      toast.error('File upload failed. Please try again.');
    } finally {
      resetFile();
    }
  };

  const { fileInputRef, handleFileSelect, handleFileChange, resetFile } =
    useExcelUpload({
      onFileSelect: handlePOLineUpload,
    });

  // PO Lines Template Download
  const handlePOLineTemplateDownload = () => {
    const downloadUrl = bulkActionApi.getTemplateDownloadUrl('po_line_create');

    window.open(downloadUrl, '_blank');
    toast.success('Successfully downloaded template.');
  };

  const handleRemovePOLines = useCallback(
    async selectedRows => {
      const poLineIds = selectedRows.map(row => row.id);

      const response = await purchaseOrderApi.deletePOLines(purchaseOrderId, {
        ids: poLineIds,
      });

      onPoLineUpdate?.();

      return {
        result: response,
        shouldRefetch: true,
      };
    },
    [onPoLineUpdate, purchaseOrderId],
  );

  const handleEditPOLines = useCallback(async selectedRows => {
    setSelectedItems(selectedRows);
    setShowEditModal(true);

    return { shouldRefetch: false };
  }, []);

  const handleModalClose = useCallback(() => {
    setShowEditModal(false);
    setSelectedItems([]);
  }, []);

  const handleSaveEdits = useCallback(
    async updatedLines => {
      try {
        await purchaseOrderApi.updatePOLines(purchaseOrderId, updatedLines);

        tableRef.current?.refetch();

        toast.success('Successfully updated PO Lines');

        onPoLineUpdate?.();
      } catch (error) {
        toast.error('Fail to update PO Lines');
      }
    },
    [onPoLineUpdate, purchaseOrderId],
  );

  const headerActions = (
    <>
      <div>
        <input
          type="file"
          ref={fileInputRef}
          className="d-none"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
        />
        <IconButton
          variant="primary"
          size="sm"
          icon="plus"
          transform="shrink-3"
          onClick={handleFileSelect}
        >
          <span className="d-none d-sm-inline-block">Import Excel</span>
        </IconButton>
      </div>

      <IconButton
        variant="falcon-default"
        size="sm"
        icon="arrow-down"
        transform="shrink-3"
        onClick={handlePOLineTemplateDownload}
      >
        <span className="d-none d-sm-inline-block">Download Template</span>
      </IconButton>
    </>
  );

  const bulkActions = [
    {
      value: 'remove_po_lines',
      label: 'Remove',
      variant: 'falcon-default',
      className: 'text-danger',
      successMessage: 'Successfully remove PO Lines',
      errorMessage: 'Fail to remove PO Lines',
      handler: handleRemovePOLines,
    },
    {
      value: 'edit_po_lines',
      label: 'Edit',
      variant: 'falcon-default',
      skipSuccessToast: true,
      errorMessage: 'Fail to load edit PO Lines',
      handler: handleEditPOLines,
    },
  ];

  const isEditableStatus = status => {
    return status === 'draft' || status === 'pending';
  };

  return (
    <>
      <Table
        ref={tableRef}
        title="Item Details (PO Line)"
        optionalHeaderComponent={
          <PurchaseOrderLineAdditionalHeader poData={poData} />
        }
        apiService={purchaseOrderApi}
        sortable={true}
        columns={PURCHASE_ORDER_LINE_LIST_COLUMNS}
        filterOptions={PURCHASE_ORDER_LINE_FILTER_OPTIONS}
        fetchConfig={{
          method: 'getPOLines',
          transformResponse: response => ({
            data: response || [],
            totalItems: response?.length || 0,
          }),
        }}
        resourceId={purchaseOrderId}
        enablePagination={false}
        bulkActions={
          isEditableStatus(poData?.po_status) ? bulkActions : undefined
        }
        headerActions={
          isEditableStatus(poData?.po_status) ? headerActions : undefined
        }
      />

      <PurchaseOrderLineEditModal
        show={showEditModal}
        onHide={handleModalClose}
        selectedItems={selectedItems}
        onSave={handleSaveEdits}
      />
    </>
  );
};

export default PurchaseOrderLines;
